import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";

// Type definitions
export interface Criterion {
  id: string;
  name?: string;
  label?: string;
  description?: string;
  maxPoints?: number;
  [key: string]: any;
}

export type ContainerSize = 'small' | 'medium' | 'large' | 'xlarge';

export interface SizeConfig {
  cardHeight: string;
  padding: string;
  titleSize: string;
  scoreSize: string;
  descriptionSize: string;
  maxPointsSize: string;
  markerSize: string;
  showDescription: boolean;
  showMarkers: boolean;
}

export interface TouchScoringCardProps {
  criterion: Criterion;
  criterionName: string;
  currentScore: number;
  maxPoints: number;
  isDarkMode: boolean;
  onScoreChange?: (score: number) => void;
  containerSize?: ContainerSize;
  isPreviewMode?: boolean;
}

export interface ProgressStyle {
  transform: string;
  transformOrigin: string;
  background: string;
}

// Container-responsive scoring card component for individual criteria
const TouchScoringCard: React.FC<TouchScoringCardProps> = React.memo(({ 
  criterion, 
  criterionName, 
  currentScore, 
  maxPoints, 
  isDarkMode, 
  onScoreChange,
  containerSize = 'medium', // small, medium, large, xlarge
  isPreviewMode = false // New prop for preview mode
}) => {
  const [visualScore, setVisualScore] = useState<number>(currentScore);
  const [isInteracting, setIsInteracting] = useState<boolean>(false);
  const [scoreChanged, setScoreChanged] = useState<boolean>(false);
  
  const cardRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<DOMRect | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastCommittedScore = useRef<number>(currentScore);
  const visualScoreRef = useRef<number>(currentScore);
  const commitTimerRef = useRef<NodeJS.Timeout | null>(null);
  const scoreChangeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isUpdatingRef = useRef<boolean>(false);

  // Pre-calculate rect on mount and resize for performance
  useEffect(() => {
    const updateRect = () => {
      if (cardRef.current) {
        rectRef.current = cardRef.current.getBoundingClientRect();
      }
    };
    
    updateRect();
    window.addEventListener('resize', updateRect, { passive: true });
    return () => window.removeEventListener('resize', updateRect);
  }, []);

  // Calculate score from position with cached rect
  const calculateScoreFromPosition = useCallback((clientX: number): number => {
    if (!rectRef.current) return visualScoreRef.current;
    
    const x = clientX - rectRef.current.left;
    const percentage = Math.max(0, Math.min(1, x / rectRef.current.width));
    return Math.round(percentage * maxPoints * 2) / 2; // Round to nearest 0.5
  }, [maxPoints]);

  // Update visual score immediately without state bouncing
  const updateVisualScore = useCallback((newScore: number, immediate: boolean = false) => {
    visualScoreRef.current = newScore;
    isUpdatingRef.current = true;
    
    const performUpdate = () => {
      setVisualScore(newScore);
      setScoreChanged(true);
      
      if (scoreChangeTimerRef.current) {
        clearTimeout(scoreChangeTimerRef.current);
      }
      scoreChangeTimerRef.current = setTimeout(() => {
        setScoreChanged(false);
        isUpdatingRef.current = false;
      }, 200);
    };
    
    if (immediate) {
      // Cancel any pending RAF for immediate update
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      performUpdate();
    } else {
      // Use RAF for smooth visual updates during interaction
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      
      rafRef.current = requestAnimationFrame(() => {
        performUpdate();
      });
    }
  }, []);

  // Commit score change with debouncing
  const commitScoreChange = useCallback(() => {
    const currentVisualScore = visualScoreRef.current;
    
    // Only commit if score actually changed and not in preview mode
    if (currentVisualScore !== lastCommittedScore.current && !isPreviewMode) {
      lastCommittedScore.current = currentVisualScore;
      
      // Clear any pending commit
      if (commitTimerRef.current) {
        clearTimeout(commitTimerRef.current);
      }
      
      // Debounce the commit
      commitTimerRef.current = setTimeout(() => {
        onScoreChange && onScoreChange(currentVisualScore);
      }, 150); // Commit after 150ms of inactivity
    }
  }, [onScoreChange, isPreviewMode]);

  // Unified pointer event handler for all input types
  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Capture pointer for consistent tracking
    if (cardRef.current && e.target === cardRef.current) {
      cardRef.current.setPointerCapture(e.pointerId);
    }
    
    setIsInteracting(true);
    
    // Update rect cache for accurate calculations
    if (cardRef.current) {
      rectRef.current = cardRef.current.getBoundingClientRect();
    }
    
    // Haptic feedback for touch devices (only if not in preview mode)
    if (!isPreviewMode && e.pointerType === 'touch' && navigator.vibrate) {
      navigator.vibrate(5); // Very short vibration
    }
    
    const newScore = calculateScoreFromPosition(e.clientX);
    updateVisualScore(newScore);
  }, [calculateScoreFromPosition, updateVisualScore, isPreviewMode]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isInteracting) return;
    
    // No preventDefault needed with pointer events
    const newScore = calculateScoreFromPosition(e.clientX);
    updateVisualScore(newScore);
  }, [isInteracting, calculateScoreFromPosition, updateVisualScore]);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Release pointer capture
    if (cardRef.current && e.target === cardRef.current) {
      cardRef.current.releasePointerCapture(e.pointerId);
    }
    
    // Ensure final score is set immediately to prevent bouncing
    const finalScore = visualScoreRef.current;
    updateVisualScore(finalScore, true); // Use immediate update
    
    setIsInteracting(false);
    commitScoreChange();
  }, [updateVisualScore, commitScoreChange]);

  const handlePointerCancel = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Handle interruptions (e.g., incoming call during touch)
    if (cardRef.current && e.target === cardRef.current) {
      cardRef.current.releasePointerCapture(e.pointerId);
    }
    
    setIsInteracting(false);
    // Revert to last committed score on cancel
    setVisualScore(lastCommittedScore.current);
    visualScoreRef.current = lastCommittedScore.current;
  }, []);

  // Sync visual score with prop changes
  useEffect(() => {
    // Only sync if not interacting and not in the middle of an update
    if (!isInteracting && !isUpdatingRef.current) {
      // Cancel any pending RAF to prevent conflicts
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      
      setVisualScore(currentScore);
      visualScoreRef.current = currentScore;
      lastCommittedScore.current = currentScore;
    }
  }, [currentScore, isInteracting]);

  // Cleanup all refs on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (commitTimerRef.current) {
        clearTimeout(commitTimerRef.current);
      }
      if (scoreChangeTimerRef.current) {
        clearTimeout(scoreChangeTimerRef.current);
      }
    };
  }, []);

  // Container-responsive sizing configuration
  const sizeConfig = useMemo((): SizeConfig => {
    const configs: Record<ContainerSize, SizeConfig> = {
      small: {
        cardHeight: '50px',
        padding: 'p-2',
        titleSize: 'text-2xs',
        scoreSize: 'text-lg',
        descriptionSize: 'text-3xs',
        maxPointsSize: 'text-3xs',
        markerSize: 'text-3xs',
        showDescription: false,
        showMarkers: false
      },
      medium: {
        cardHeight: '70px',
        padding: 'p-3',
        titleSize: 'text-xs',
        scoreSize: 'text-2xl',
        descriptionSize: 'text-2xs',
        maxPointsSize: 'text-xs',
        markerSize: 'text-2xs',
        showDescription: true,
        showMarkers: true
      },
      large: {
        cardHeight: '85px',
        padding: 'p-4',
        titleSize: 'text-sm',
        scoreSize: 'text-3xl',
        descriptionSize: 'text-xs',
        maxPointsSize: 'text-sm',
        markerSize: 'text-xs',
        showDescription: true,
        showMarkers: true
      },
      xlarge: {
        cardHeight: '100px',
        padding: 'p-5',
        titleSize: 'text-base',
        scoreSize: 'text-4xl',
        descriptionSize: 'text-sm',
        maxPointsSize: 'text-base',
        markerSize: 'text-sm',
        showDescription: true,
        showMarkers: true
      }
    };
    return configs[containerSize] || configs.medium;
  }, [containerSize]);

  // Memoized calculations for better performance
  const percentage = useMemo((): number => 
    maxPoints > 0 ? (visualScore / maxPoints) * 100 : 0, 
    [visualScore, maxPoints]
  );
  
  const scoreColor = useMemo((): string => {
    if (percentage >= 90) return isDarkMode ? "text-green-400" : "text-green-600";
    if (percentage >= 75) return isDarkMode ? "text-blue-400" : "text-blue-600";
    if (percentage >= 60) return isDarkMode ? "text-yellow-400" : "text-yellow-600";
    return isDarkMode ? "text-red-400" : "text-red-600";
  }, [percentage, isDarkMode]);

  // Use CSS transform for better performance than width animation
  const progressStyle = useMemo((): ProgressStyle => ({
    transform: `scaleX(${percentage / 100})`,
    transformOrigin: 'left',
    background: `linear-gradient(to right, rgb(236 72 153 / 0.3) 0%, rgb(147 51 234 / 0.3) 100%)`
  }), [percentage]);

  return (
    <div
      ref={cardRef}
      className={`relative overflow-hidden rounded-xl border-2 transition-all duration-100 cursor-pointer select-none ${
        isInteracting 
          ? "border-pink-400 bg-pink-500/20 shadow-lg scale-[0.98]"
          : isDarkMode
            ? "border-gray-600 bg-gray-800/50 hover:border-gray-500 hover:bg-gray-800"
            : "border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50"
      } ${isPreviewMode ? 'opacity-90' : ''}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      style={{ 
        minHeight: sizeConfig.cardHeight,
        touchAction: 'pan-y', // Allow vertical scrolling but capture horizontal
        userSelect: 'none',
        WebkitUserSelect: 'none',
        willChange: isInteracting ? 'transform' : 'auto'
      }}
    >
      {/* Progress fill background - use transform for GPU acceleration */}
      <div 
        className="absolute inset-0 will-change-transform"
        style={{
          ...progressStyle,
          transition: isInteracting ? 'none' : 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      />
      
      {/* Content */}
      <div className={`relative z-10 ${sizeConfig.padding}`}>
        <div className="flex justify-between items-center h-full">
          <div className="flex-1 pr-2 sm:pr-4 min-w-0">
            <h4 className={`font-bold ${sizeConfig.titleSize} uppercase tracking-wide truncate ${
              isDarkMode ? "text-white" : "text-gray-800"
            }`}>
              {criterionName}
            </h4>
            {sizeConfig.showDescription && criterion.description && (
              <p className={`${sizeConfig.descriptionSize} mt-0.5 leading-tight line-clamp-2 ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}>
                {criterion.description}
              </p>
            )}
          </div>
          
          <div className="text-right flex-shrink-0">
            <div className={`${sizeConfig.scoreSize} font-bold transition-transform duration-200 will-change-transform ${scoreColor} ${
              scoreChanged ? 'scale-110' : 'scale-100'
            }`}>
              {visualScore.toFixed(1)}
            </div>
            <div className={`${sizeConfig.maxPointsSize} ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}>
              of {maxPoints}
            </div>
          </div>
        </div>
      </div>
      
      {/* Subtle score markers for reference */}
      {sizeConfig.showMarkers && (
        <div className="absolute bottom-1 left-2 right-2 flex justify-between pointer-events-none opacity-60">
          {Array.from({ length: Math.floor(maxPoints / 5) + 1 }, (_, i) => {
            const value = i * 5;
            if (value > maxPoints) return null;
            return (
              <div 
                key={value}
                className={`${sizeConfig.markerSize} ${
                  isDarkMode ? "text-gray-600" : "text-gray-400"
                }`}
              >
                {value}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Preview mode indicator */}
      {isPreviewMode && (
        <div className="absolute top-1 right-1 opacity-50">
          <div className={`${sizeConfig.markerSize} ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          } bg-black/20 rounded px-1`}>
            PREVIEW
          </div>
        </div>
      )}
    </div>
  );
});

TouchScoringCard.displayName = 'TouchScoringCard';

export default TouchScoringCard;
